import { createRoot, type Root } from 'react-dom/client'

export interface SubgeoTooltipProps {
	name: string
}

export const SubgeoTooltip = ({ name }: SubgeoTooltipProps) => (
	<div className='pointer-events-none min-w-[180px] max-w-[280px] rounded-xl bg-neutral-white p-2 shadow-lg'>
		<h3 className='m-0 w-full overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-neutral-900 text-sm'>
			{name}
		</h3>
	</div>
)

const tooltipRoots = new Map<HTMLDivElement, Root>()
const rootPool: Array<{ container: HTMLDivElement; root: Root }> = []
const MAX_POOL_SIZE = 30

export const createSubgeoTooltipElement = (name: string): HTMLDivElement => {
	let container: HTMLDivElement
	let root: Root

	if (rootPool.length > 0) {
		const pooled = rootPool.pop() as {
			container: HTMLDivElement
			root: Root
		}
		container = pooled.container
		root = pooled.root
	} else {
		container = document.createElement('div')
		root = createRoot(container)
	}

	tooltipRoots.set(container, root)

	root.render(<SubgeoTooltip name={name} />)

	return container
}

export const cleanupTooltipRoots = (): void => {
	const currentRoots = Array.from(tooltipRoots.entries())
	tooltipRoots.clear()

	for (const [container, root] of currentRoots) {
		if (rootPool.length < MAX_POOL_SIZE) {
			rootPool.push({ container, root })
		} else {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount tooltip root:', e)
			}
		}
	}
}

export const destroyAllTooltips = (): void => {
	const allCurrentRoots = Array.from(tooltipRoots.values())
	const allPooledRoots = rootPool.map(({ root }) => root)

	tooltipRoots.clear()
	rootPool.length = 0

	requestIdleCallback(() => {
		for (const root of allCurrentRoots) {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount tooltip root:', e)
			}
		}

		for (const root of allPooledRoots) {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount pooled root:', e)
			}
		}
	})
}
